import dayjs from 'dayjs';
import {
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';

import { OrderList, ScheduleList } from '@/components/Order';
import ScheduleSearch from '@/components/Order/ScheduleList/ScheduleSearch/ScheduleSearch.vue';
import { SET_DEPARTMENT_LIST } from '@/store/orders/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import bus from '@/utils/bus';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  ORDER_STATUS_WITH_TYPE_TEXT,
  PER_PAGE_OPTIONS,
  SNACKBAR_TYPES,
} from '@/utils/constants';

export default {
  name: 'Order',
  components: {
    OrderList,
    ScheduleSearch,
    ScheduleList,
  },
  props: {
    action: {
      type: String,
      default: 'list',
    },
  },
  data() {
    return {
      currentTab: ORDER_STATUS_WITH_TYPE_TEXT.ONGOING,
      dates: [],
      dateType: 'start',
      page: FIRST_PAGE,
      perPage: DEFAULT_PER_PAGE,
      search: '',
      sortField: 'start',
      sortOrder: 'desc',
    };
  },
  computed: {
    ...mapState('orders', {
      gettingSchedules: ({ gettingSchedules }) => gettingSchedules,
      schedulesInfos: ({ schedules }) => schedules,
    }),
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    isValidQueryTab() {
      return Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(this.queryTab);
    },
    lastPage() {
      return this.pagination?.total_page || 1;
    },
    loading() {
      return this.gettingSchedules;
    },
    noMorePages() {
      return this.page >= this.lastPage;
    },
    pagination() {
      return this.schedulesInfos?.pagination || {};
    },
    total() {
      return this.schedulesInfos?.pagination?.total || 0;
    },
    schedules() {
      return this.schedulesInfos?.data?.list || [];
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async created() {
    bus.$on('GET_SCHEDULES', async () => {
      await this.setPageOrGetData();
    });

    if (this.isValidQueryTab) {
      this.currentTab = this.queryTab;
    }

    await this.loadSchedules();
  },
  destroyed() {
    bus.$off('GET_SCHEDULES');
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    this.loadDepartmentInfo();
  },
  watch: {
    page() {
      this.loadSchedules();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
    currentTab(newVal, oldVal) {
      const isValidAndNewTab = this.isValidQueryTab && newVal !== this.queryTab;
      if ((!this.queryTab && newVal !== oldVal) || isValidAndNewTab) {
        this.setPageOrGetData();

        if (!this.queryTab || this.isValidQueryTab) {
          this.$router.push({ query: { tab: newVal } });
        }
      }
    },
  },
  methods: {
    ...mapActions('orders', {
      getSchedules: 'GET_SCHEDULES',
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    ...mapMutations('orders', {
      setDepartmentList: SET_DEPARTMENT_LIST,
    }),
    async loadDepartmentInfo() {
      try {
        const departments = await this.getDepartmentList();
        this.setDepartmentList(departments);
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '讀取部門時時發生錯誤',
          msg,
        });
      }
    },
    async loadSchedules() {
      try {
        const [startDate, endDate] = this.dates;
        const query = {
          date_query_type: this.dateType,
          end_time: endDate ? dayjs(endDate).endOf('day').format('YYYY-MM-DD HH:mm') : '',
          keyword: this.search,
          order: this.sortOrder,
          page: this.page,
          per_page: this.perPage,
          sort_by: this.sortField,
          start_time: startDate ? dayjs(startDate).startOf('day').format('YYYY-MM-DD HH:mm') : '',
        };

        await this.getSchedules(query);
      } catch (error) {
        const { message: msg = '' } = error;
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '讀取團購時發生錯誤',
          msg,
        });
      }
    },
    onClearSearch() {
      this.search = '';
      this.dates = [];
      this.setPageOrGetData();
    },
    onCreateNew() {
      this.$router.push({
        path: '/order/schedule/create',
      });
    },
    onDatesUpdates(dates) {
      this.dates = dates;
    },
    onDateTypeUpdated(type) {
      this.dateType = type;
    },
    onSearch() {
      this.setPageOrGetData();
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.loadSchedules();
    },
    goBack() {
      const { query: { tab } = {} } = this.$route || {};
      const isValidTab = Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(tab);
      const query = isValidTab ? { tab } : {};
      this.$router.push({
        path: '/order/list',
        query,
      });
    },
    setPageOrGetData() {
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.loadSchedules();
      }
    },
  },
};
